"use client";

import { c as _c } from "react/compiler-runtime";
import { ResultOf } from "@graphql-typed-document-node/core";
import React, { type ReactNode, useState, useRef, useCallback, ViewTransition } from "react";
import type { FragmentOfShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { Image } from "@repo/components/atoms/image";
import { Link } from "@repo/components/atoms/link";
import { Video } from "@repo/components/atoms/video";
import { readFragmentShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { Video_Fragment } from "#/graphql/shopify-storefront/fragments";
import { cn, thumbhashDataURL } from "@repo/lib";
import { getMediaQueryForURL } from "@repo/shopify/utilities/media-query";
import type { transformShopifyImageToNextImage } from "@repo/shopify/utilities/next";
import { LinkedDataProductGroup } from "#/components/linked-data/product-group";
import { PriceAndCompareAtPrice } from "#/components/molecules/price-and-compare-at-price";
import { GridItem_Product_Fragment, GridItem_Localization_Fragment, GridItem_Shop_Fragment } from "./grid-item";

// NOTE: These types and fragments would be imported from the original GridItem file or a shared file.

const initialState = {
  src: undefined,
  pip: false,
  playing: false,
  controls: false,
  light: false,
  volume: 1,
  muted: false,
  played: 0,
  loaded: 0,
  duration: 0,
  playbackRate: 1.0,
  loop: false,
  seeking: false,
  loadedSeconds: 0,
  playedSeconds: 0
};
type PlayerState = Omit<typeof initialState, "src"> & {
  src?: string;
};

// Define the shape of props expected from the Server Component
interface GridItemClientProps {
  className?: string;
  delay?: number;
  hide?: boolean;
  lang: Navigator["language"];
  loading?: "eager" | "lazy";
  preload?: boolean;
  sizes?: string[];
  primaryImage: ReturnType<typeof transformShopifyImageToNextImage> | undefined;
  secondaryImage: ReturnType<typeof transformShopifyImageToNextImage> | undefined;
  video?: ResultOf<typeof Video_Fragment>;
  productMaskedSuper: FragmentOfShopifyStorefront<typeof GridItem_Product_Fragment>;
  localizationMasked: FragmentOfShopifyStorefront<typeof GridItem_Localization_Fragment>;
  shopMasked: FragmentOfShopifyStorefront<typeof GridItem_Shop_Fragment>;
}
export function GridItemClient(t0) {
  const $ = _c(61);
  const {
    className,
    delay: t1,
    hide: t2,
    lang,
    loading,
    localizationMasked,
    preload,
    shopMasked,
    sizes,
    primaryImage,
    secondaryImage,
    video,
    productMaskedSuper
  } = t0;
  const delay = t1 === undefined ? 0 : t1;
  const hide = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== localizationMasked) {
    t3 = readFragmentShopifyStorefront(GridItem_Localization_Fragment, localizationMasked);
    $[0] = localizationMasked;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const localization = t3;
  const product = readFragmentShopifyStorefront(GridItem_Product_Fragment, productMaskedSuper);
  let t4;
  if ($[2] !== shopMasked) {
    t4 = readFragmentShopifyStorefront(GridItem_Shop_Fragment, shopMasked);
    $[2] = shopMasked;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const shop = t4;
  const playerRef = useRef(null);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = player => {
      if (!player) {
        return;
      }
      playerRef.current = player;
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const setPlayerRef = t5;
  const [, setState] = useState(initialState);
  let t6;
  if ($[5] !== setState || $[6] !== video?.sources.length) {
    t6 = () => {
      if (!video?.sources.length) {
        return;
      }
      setState(_temp);
      playerRef.current?.play();
    };
    $[5] = setState;
    $[6] = video?.sources.length;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handlePlay = t6;
  let t7;
  if ($[8] !== setState || $[9] !== video?.sources.length) {
    t7 = () => {
      if (!video?.sources.length) {
        return;
      }
      setState(_temp2);
      playerRef.current?.pause();
      playerRef.current?.load();
    };
    $[8] = setState;
    $[9] = video?.sources.length;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handlePause = t7;
  if (hide) {
    return null;
  }
  let t8;
  if ($[11] !== className) {
    t8 = cn("group relative flex h-full w-full flex-col", "focus-visible:ring-offset-white", className);
    $[11] = className;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = product.id;
  const t10 = product.handle;
  const t11 = product.title;
  const t12 = product.priceRange.minVariantPrice?.amount;
  const t13 = product.priceRange.minVariantPrice?.currencyCode;
  const t14 = product.tags.join(", ");
  const t15 = `/products/${product.handle}`;
  let t16;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cn("mb-0 mt-0 overflow-hidden contain-[inline-size_layout_style]", "aspect-2/3 relative mb-4 w-full overflow-hidden", "group");
    $[13] = t16;
  } else {
    t16 = $[13];
  }
  let t17;
  if ($[14] !== video?.sources) {
    t17 = video?.sources.filter(_temp3).map(_temp4);
    $[14] = video?.sources;
    $[15] = t17;
  } else {
    t17 = $[15];
  }
  let t18;
  if ($[16] !== setPlayerRef || $[17] !== t17) {
    t18 = <Video className="video absolute inset-0" loop={true} muted={true} playsInline={true} ref={setPlayerRef}>{t17}</Video>;
    $[16] = setPlayerRef;
    $[17] = t17;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  let t19;
  if ($[19] !== className || $[20] !== delay || $[21] !== product.title || $[22] !== secondaryImage || $[23] !== sizes) {
    t19 = secondaryImage?.src && <ViewTransition name={`${secondaryImage.id}-instance-0`}><Image className={cn("absolute h-full w-full object-cover secondary opacity-0 group-hover:opacity-100 group-focus:opacity-100", className)} alt={secondaryImage.alt ?? product.title} blurDataURL={secondaryImage.thumbhash ? thumbhashDataURL(secondaryImage.thumbhash) : undefined} delay={delay} fill={true} height={undefined} loading="lazy" sizes={sizes?.join(", ")} src={secondaryImage.src} width={undefined} /></ViewTransition>;
    $[19] = className;
    $[20] = delay;
    $[21] = product.title;
    $[22] = secondaryImage;
    $[23] = sizes;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== className || $[26] !== delay || $[27] !== loading || $[28] !== preload || $[29] !== primaryImage || $[30] !== product.title || $[31] !== sizes) {
    t20 = primaryImage?.src && <ViewTransition name={`${primaryImage.id}-instance-0`}><Image className={cn("absolute h-full w-full object-cover primary opacity-100 group-hover:opacity-0 group-focus:opacity-0", className)} alt={primaryImage.alt ?? product.title ?? ""} blurDataURL={primaryImage.thumbhash ? thumbhashDataURL(primaryImage.thumbhash) : undefined} delay={delay} fill={true} height={undefined} loading={loading} preload={preload} sizes={sizes?.join(", ")} src={primaryImage.src} width={undefined} /></ViewTransition>;
    $[25] = className;
    $[26] = delay;
    $[27] = loading;
    $[28] = preload;
    $[29] = primaryImage;
    $[30] = product.title;
    $[31] = sizes;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t18 || $[34] !== t19 || $[35] !== t20) {
    t21 = <figure className={t16}>{t18}{t19}{t20}</figure>;
    $[33] = t18;
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== t15 || $[38] !== t21) {
    t22 = <Link href={t15}>{t21}</Link>;
    $[37] = t15;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== lang || $[41] !== product) {
    t23 = product.priceRange.minVariantPrice ? <footer><ViewTransition name={`${product.id}-title-lg`}><h3 className={cn("mb-2 box-decoration-clone text-xs")}><Link href={`/products/${product.handle}`}>{product.title}</Link></h3></ViewTransition><ViewTransition name={`${product.id}-price-lg`}><PriceAndCompareAtPrice className="text-xs md:grid-flow-col" lang={lang} productMasked={product} /></ViewTransition></footer> : null;
    $[40] = lang;
    $[41] = product;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== localization || $[44] !== product || $[45] !== shop) {
    t24 = <LinkedDataProductGroup id="LinkedDataProductGroup" localizationMasked={localization} productMasked={product} shopMasked={shop} />;
    $[43] = localization;
    $[44] = product;
    $[45] = shop;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== handlePause || $[48] !== handlePlay || $[49] !== lang || $[50] !== product.handle || $[51] !== product.id || $[52] !== product.title || $[53] !== t12 || $[54] !== t13 || $[55] !== t14 || $[56] !== t22 || $[57] !== t23 || $[58] !== t24 || $[59] !== t8) {
    t25 = <article className={t8} data-product-id={t9} data-product-handle={t10} data-product-title={t11} data-product-price={t12} data-product-currency={t13} data-product-tags={t14} data-product-lang={lang} onMouseEnter={handlePlay} onMouseLeave={handlePause}>{t22}{t23}{t24}</article>;
    $[47] = handlePause;
    $[48] = handlePlay;
    $[49] = lang;
    $[50] = product.handle;
    $[51] = product.id;
    $[52] = product.title;
    $[53] = t12;
    $[54] = t13;
    $[55] = t14;
    $[56] = t22;
    $[57] = t23;
    $[58] = t24;
    $[59] = t8;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  return t25;
}
function _temp4(source_0) {
  return <source key={`${source_0.format}-${source_0.url}`} media={getMediaQueryForURL(source_0.url)} src={source_0.url} type={`video/${source_0.format}`} />;
}
function _temp3(source) {
  return source.format !== "m3u8";
}
function _temp2(prevState_0) {
  return {
    ...prevState_0,
    playing: false
  };
}
function _temp(prevState) {
  return {
    ...prevState,
    playing: true
  };
}